import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import moment from 'moment';

import { TimeService } from '@bayclubs/utils';
import { debounceTime } from 'rxjs/operators';
import { camps } from '../../../models/models.generated';
import { StatisticsApiService } from '../../../services/api/statistics-api.service';
import { DashboardFilterService } from '../dashboard-filter/dashboard-filter.service';

@Component({
    selector: 'app-statistics-chart',
    templateUrl: './statistics-chart.component.html',
    styleUrls: ['./statistics-chart.component.scss'],
    standalone: false
})
export class StatisticsChartComponent implements OnInit, AfterViewInit {
    @Input()
    public filterForm: UntypedFormGroup;

    chart: any = [];

    public items: camps.TransactionsDataItem[];

    private labels: Array<string> = [];
    private soldSessions: Array<number> = [];
    private transactions: Array<number> = [];

    constructor(
        private translate: TranslateService,
        private statisticsApi: StatisticsApiService,
        private dashboardFilterService: DashboardFilterService,
        private tmie: TimeService
    ) {}

    ngOnInit() {}

    ngAfterViewInit() {
        this.loadData();

        this.filterForm.valueChanges.pipe(debounceTime(100)).subscribe(() => {
            this.items = null;
            this.loadData();
        });
    }

    private loadData(): void {
        const filter = this.dashboardFilterService.getFilterResult(
            this.filterForm.controls['dateRange'].value
        );

        const campType = this.filterForm.controls['campType'].value;

        this.statisticsApi
            .getTransactionsOverview(<camps.ChartDataRequest>{
                dateFrom: this.tmie.toBcDate(filter.dateFrom),
                dateTo: this.tmie.toBcDate(filter.dateTo),
                timezone: this.tmie.defaultTimezone(),
                campTypes: campType ? [campType] : [],
            })
            .subscribe((response) => {
                this.items = response.value.data;

                this.prepareChartData();
                this.drawChart();
            });
    }

    public get isAnyValue(): boolean {
        if (!this.items) {
            return true;
        }
        return (
            this.items.filter((x) => x.soldSessions > 0 || x.transactions > 0)
                .length > 0
        );
    }

    private prepareChartData(): void {
        this.labels = [];
        this.soldSessions = [];
        this.transactions = [];

        for (const item of this.items) {
            this.labels.push(moment(item.date.value).format('MMM Do'));
            this.soldSessions.push(item.soldSessions);
            this.transactions.push(item.transactions);
        }
    }

    getStepSize(): number {
        const maxValue = Math.max(...this.soldSessions);

        const stepSize = Math.round(maxValue / 10);

        const prefferedStepSizes = [1, 2, 5, 10, 20, 50, 100];
        const diffs = prefferedStepSizes.map((x) => Math.abs(x - stepSize));
        const minDiff = Math.min(...diffs);
        const minDiffIndex = diffs.indexOf(minDiff);

        return prefferedStepSizes[minDiffIndex];
    }

    private drawChart(): void {
        const stepSize = this.getStepSize();

        const config = {
            type: 'line',
            data: {
                labels: this.labels,
                datasets: [
                    {
                        label: this.translate.instant(
                            'StatisticsChart.SoldSessions'
                        ),
                        backgroundColor: '#3cba9f',
                        borderColor: '#3cba9f',
                        data: this.soldSessions,
                        fill: false,
                    },
                    {
                        label: this.translate.instant(
                            'StatisticsChart.Transactions'
                        ),
                        fill: false,
                        backgroundColor: '#ffcc00',
                        borderColor: '#ffcc00',
                        data: this.transactions,
                    },
                ],
            },
            options: {
                responsive: true,

                tooltips: {
                    mode: 'index',
                    intersect: false,
                },
                hover: {
                    mode: 'nearest',
                    intersect: true,
                },
                scales: {
                    xAxes: [
                        {
                            display: true,
                        },
                    ],
                    yAxes: [
                        {
                            display: true,
                            ticks: {
                                beginAtZero: true,
                                stepSize: stepSize,
                            },
                        },
                    ],
                },
            },
        };

        if (this.chart && this.chart.destroy) {
            this.chart.destroy();
        }
        this.chart = new Chart('canvas', config);
    }
}
